// apps/web/src/features/commercial-proposals/useCommercialProposalPhotoUpload.ts
//
// @aiPurpose presigned-PUT upload flow for the CP product photo (#1207, #1244):
//   reject SVG (clipboard/file) → compress to JPEG →
//   check the COMPRESSED blob against the 10 MiB cap → request a presigned
//   upload URL scoped to `commercial-proposal-photo` → PUT the compressed
//   JPEG → return the issued `objectKey` for the form to bind.
// @aiSecurity the type/size gate mirrors the backend's scope policy. The
//   server re-validates both, so this is a UX shortcut, not the source of
//   truth — a forged type/size still 4xxs at the signed URL / controller.
import { useState } from 'react';

import { requestUploadUrl } from '../../api/requests/index.js';
import {
  compressImage,
  imageSourceFormat,
  isPhotoCompressError,
  isUnsupportedSvgImage,
} from '../requests/draft-media/index.js';

export const CP_PHOTO_ACCEPT_HEADER = 'image/*';
export const CP_PHOTO_MAX_BYTES = 10 * 1024 * 1024;

export type CommercialProposalPhotoUploadError =
  | 'tooLarge'
  | 'wrongType'
  | 'decodeFailed'
  | 'encodeFailed'
  | 'uploadFailed';

export interface UseCommercialProposalPhotoUploadResult {
  uploading: boolean;
  error: CommercialProposalPhotoUploadError | null;
  errorFormat: string;
  /** Reject SVG, then compress + upload. Resolves to the issued objectKey, or null
   *  on a pre-check / network failure (the failure is surfaced via `error`). */
  upload: (file: File) => Promise<string | null>;
  clearError: () => void;
}

export function useCommercialProposalPhotoUpload(): UseCommercialProposalPhotoUploadResult {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<CommercialProposalPhotoUploadError | null>(null);
  const [errorFormat, setErrorFormat] = useState('');

  const upload = async (file: File): Promise<string | null> => {
    if (isUnsupportedSvgImage(file)) {
      setError('wrongType');
      setErrorFormat('');
      return null;
    }
    setError(null);
    setErrorFormat('');
    setUploading(true);
    try {
      const blob = await compressImage(file);
      if (blob.size > CP_PHOTO_MAX_BYTES) {
        setError('tooLarge');
        return null;
      }
      const signed = await requestUploadUrl({
        contentType: 'image/jpeg',
        contentLength: blob.size,
        scope: 'commercial-proposal-photo',
      });
      const res = await fetch(signed.uploadUrl, {
        method: 'PUT',
        body: blob,
        headers: { 'content-type': 'image/jpeg' },
      });
      if (!res.ok) throw new Error(`upload failed: ${res.status}`);
      return signed.objectKey;
    } catch (cause) {
      // A compress/presign/PUT failure must surface, not silently no-op; the
      // control shows this as an inline upload error and keeps the prior photo.
      if (isPhotoCompressError(cause) && cause.code === 'decode-failed') {
        setError('decodeFailed');
        setErrorFormat(imageSourceFormat(file));
      } else if (isPhotoCompressError(cause) && cause.code === 'encode-failed') {
        setError('encodeFailed');
      } else {
        setError('uploadFailed');
      }
      return null;
    } finally {
      setUploading(false);
    }
  };

  return {
    uploading,
    error,
    errorFormat,
    upload,
    clearError: () => {
      setError(null);
      setErrorFormat('');
    },
  };
}
